import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NoteDialog } from '@/components/ui/note-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/lib/apiClient'
import type { Order as ApiOrder } from '@/types/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  CreditCard,
  Eye,
  Loader2,
  MapPin,
  Package,
  PlusCircle,
  Tag,
  Truck,
  Upload,
  User,
  XCircle,
} from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface OrderItem {
  id: string
  productId: string
  productName: string
  productSku: string
  quantity: number
  price: number
  total: number
  image?: string
}

interface Order {
  id: string
  orderNumber: string
  customerName: string
  customerEmail: string
  customerPhone: string
  status: 'pending' | 'review' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded' | 'review'
  paymentMethod: string
  subtotal: number
  shippingAmount: number
  tax: number
  discount: number
  total: number
  items: OrderItem[]
  payments?: Array<{
    id: string
    status: string
  }>
  shippingAddress: {
    fullName: string
    phone: string
    street: string
    city: string
    province: string
    postalCode: string
    country: string
  }
  billingAddress: {
    street: string
    city: string
    province: string
    postalCode: string
    country: string
  }
  orderDate: string
  estimatedDelivery?: string
  deliveredDate?: string
  trackingNumber?: string
  carrier?: string
  courier?: string
  shipping?: string
  paymentProof?: string
  notes?: string
  voucherCode?: string
  latest_payment?: {
    proof_image_url?: string
    proof_image_path?: string
    unique_number?: number
  }
}

interface OrderDetailProps {
  className?: string
  orderId: string
  onBack?: () => void
  onUpdateStatus: (_orderId: string, _status: Order['status']) => void
  onUpdateTracking: (_orderId: string, _trackingNumber: string, _carrier: string) => void
  onAddNote: (_orderId: string, _note: string) => void
}

const mapApiOrderToComponent = (apiOrder: ApiOrder): Order => {
  const user = apiOrder.user
  const shippingAddress = apiOrder.shipping_address
  const billingAddress = apiOrder.billing_address
  const payment = apiOrder.payments?.[0]

  // Type assertion to handle shipping_cost property
  const orderWithShippingCost = apiOrder as ApiOrder & {
    shipping_cost: number
  }

  // Extract tracking number from notes if it exists there, or use direct field
  let trackingNumber = apiOrder.tracking_number
  if (!trackingNumber && apiOrder.notes) {
    const trackingMatch = apiOrder.notes.match(/tracking[:\s]+([A-Z0-9-]+)/i)
    if (trackingMatch) {
      trackingNumber = trackingMatch[1]
    }
  }

  // Get payment proof URL from the latest payment
  const paymentProofUrl =
    apiOrder.latest_payment?.proof_image_url || payment?.proof_image_url || undefined

  return {
    id: apiOrder.id,
    orderNumber: apiOrder.order_number,
    customerName: user?.full_name || 'N/A',
    customerEmail: user?.email || 'N/A',
    customerPhone: user?.phone || shippingAddress?.phone || 'N/A',
    status: apiOrder.status as Order['status'],
    paymentStatus: apiOrder.payment_status as Order['paymentStatus'],
    paymentMethod: payment?.payment_method || 'N/A',
    subtotal: orderWithShippingCost.subtotal,
    shippingAmount: orderWithShippingCost.shipping_cost || 0,
    tax: orderWithShippingCost.tax_amount,
    discount: apiOrder.discount_amount || 0,
    total: apiOrder.total_amount,
    payments: apiOrder.payments || [],
    items: apiOrder.items.map((item) => ({
      id: item.id,
      productId: item.product_id,
      productName: item.product?.name || 'N/A',
      productSku: item.product?.sku || 'N/A',
      quantity: item.quantity,
      price: item.unit_price,
      total: item.total_price,
      image: item.product?.images?.[0]?.image_url,
    })),
    shippingAddress: shippingAddress
      ? {
          fullName: shippingAddress.full_name || '',
          phone: shippingAddress.phone || '',
          street: shippingAddress.address || shippingAddress.address_line_1 || '',
          city: shippingAddress.city || '',
          province: shippingAddress.state || shippingAddress.province || '',
          postalCode: shippingAddress.postal_code || shippingAddress.zip_code || '',
          country: shippingAddress.country || 'Indonesia',
        }
      : {
          fullName: 'N/A',
          phone: 'N/A',
          street: 'N/A',
          city: 'N/A',
          province: 'N/A',
          postalCode: 'N/A',
          country: 'N/A',
        },
    billingAddress: billingAddress
      ? {
          street: billingAddress.address || billingAddress.address_line_1 || '',
          city: billingAddress.city || '',
          province: billingAddress.state || billingAddress.province || '',
          postalCode: billingAddress.postal_code || billingAddress.zip_code || '',
          country: billingAddress.country || 'Indonesia',
        }
      : {
          street: 'N/A',
          city: 'N/A',
          province: 'N/A',
          postalCode: 'N/A',
          country: 'N/A',
        },
    orderDate: new Date(apiOrder.created_at).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    estimatedDelivery: apiOrder.estimated_delivery
      ? new Date(apiOrder.estimated_delivery).toLocaleDateString('id-ID', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : undefined,
    deliveredDate: apiOrder.delivered_at
      ? new Date(apiOrder.delivered_at).toLocaleDateString('id-ID', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : undefined,
    trackingNumber: trackingNumber || undefined,
    carrier: apiOrder.carrier || undefined,
    courier: apiOrder.courier || undefined,
    shipping: apiOrder.courier_service || undefined,
    paymentProof: paymentProofUrl,
    notes: apiOrder.notes,
    voucherCode: apiOrder.voucher_code || undefined,
    latest_payment: apiOrder.latest_payment,
  }
}

export const OrderDetail: React.FC<OrderDetailProps> = ({
  className,
  orderId,
  onBack,
  onUpdateStatus,
  onUpdateTracking,
  onAddNote,
}) => {
  const navigate = useNavigate()
  const [isEditingStatus, setIsEditingStatus] = useState(false)
  const [newTrackingNumber, setNewTrackingNumber] = useState('')
  const [isPaymentProofModalOpen, setIsPaymentProofModalOpen] = useState(false)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadPreview, setUploadPreview] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: orderResponse, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => apiClient.getOrder(orderId),
  })

  const order = useMemo(() => {
    if (!orderResponse?.data) return null
    return mapApiOrderToComponent(orderResponse.data)
  }, [orderResponse])

  // Update tracking number input when order data changes
  React.useEffect(() => {
    if (order?.trackingNumber) {
      setNewTrackingNumber(order.trackingNumber)
    }
  }, [order?.trackingNumber])

  // Upload payment proof mutation
  const uploadPaymentProofMutation = useMutation({
    mutationFn: async ({ paymentId, file }: { paymentId: string; file: File }) => {
      return apiClient.adminUploadPaymentProof(paymentId, file)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      setIsUploadDialogOpen(false)
      setSelectedFile(null)
      setUploadPreview(null)
      // Show success message (you might want to use a toast here)
      alert('Payment proof uploaded successfully')
    },
    onError: () => {
      // Show error message (you might want to use a toast here)
      alert('Failed to upload payment proof. Please try again.')
    },
  })

  const handleBackClick = () => {
    if (onBack) {
      onBack()
    } else {
      navigate('/orders')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading order details...</span>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">Order not found</div>
      </div>
    )
  }

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />
      case 'review':
        return <Eye className="h-4 w-4" />
      case 'processing':
        return <Package className="h-4 w-4" />
      case 'shipped':
        return <Truck className="h-4 w-4" />
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />
      case 'cancelled':
        return <XCircle className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const formatPaymentMethod = (method: string) => {
    if (!method || method === 'N/A') return 'N/A'
    return method
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  const handleStatusUpdate = (newStatus: Order['status']) => {
    onUpdateStatus(orderId, newStatus)
    setIsEditingStatus(false)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setUploadPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUploadPaymentProof = () => {
    if (selectedFile && order?.payments?.[0]) {
      uploadPaymentProofMutation.mutate({
        paymentId: order.payments[0].id,
        file: selectedFile,
      })
    }
  }

  return (
    <div className={className}>
      {/* Header */}
      <div className="space-y-3 mb-6">
        <Button variant="ghost" size="sm" onClick={handleBackClick}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">{order.orderNumber}</h1>
            <p className="text-muted-foreground">Order placed on {order.orderDate}</p>
          </div>
          <div className="flex items-center space-x-2">
            <div
              className={`inline-flex h-9 items-center rounded-md border px-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 gap-1 ${
                order.status === 'delivered'
                  ? 'border-input bg-background text-muted-foreground'
                  : order.status === 'cancelled'
                    ? 'border-destructive bg-destructive/10 text-destructive'
                    : order.status === 'shipped'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : order.status === 'processing'
                        ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                        : order.status === 'review'
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-500 bg-gray-50 text-gray-700'
              }`.replace(/\s+/g, ' ')}
            >
              {getStatusIcon(order.status)}
              {order.status}
            </div>
          </div>
        </div>
      </div>

      {/* Status Update */}
      {isEditingStatus && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Update Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex space-x-2">
              {/* Removed getStatusOptions as it's no longer used */}
              {/* The options for status update are now hardcoded */}
              <Button variant="outline" onClick={() => handleStatusUpdate('processing')}>
                Processing
              </Button>
              <Button variant="outline" onClick={() => handleStatusUpdate('cancelled')}>
                Cancelled
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle>Order Items</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.productName}
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <div>
                            <div className="font-medium">{item.productName}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.productSku}</TableCell>
                      <TableCell className="text-right">{formatPrice(item.price)}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatPrice(item.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Order Summary */}
              <div className="mt-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span className="text-right">{formatPrice(order.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Shipping</span>
                  <span className="text-right">{formatPrice(order.shippingAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax</span>
                  <span className="text-right">{formatPrice(order.tax)}</span>
                </div>
                {order.latest_payment?.unique_number && (
                  <div className="flex justify-between text-sm">
                    <span>Unique Code</span>
                    <span className="text-right">
                      {formatPrice(order.latest_payment.unique_number)}
                    </span>
                  </div>
                )}
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      Discount {order.voucherCode && `(${order.voucherCode})`}
                    </span>
                    <span className="text-right">-{formatPrice(order.discount)}</span>
                  </div>
                )}
                <div className="border-t pt-2">
                  <div className="flex justify-between font-medium text-lg">
                    <span>Total</span>
                    <span className="text-right">{formatPrice(order.total)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Order Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <div>
                    <div className="font-medium">Order Placed</div>
                    <div className="text-sm text-muted-foreground">{order.orderDate}</div>
                  </div>
                </div>
                {order.paymentStatus === 'paid' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <div className="font-medium">Payment Confirmed</div>
                      <div className="text-sm text-muted-foreground">{order.orderDate}</div>
                    </div>
                  </div>
                )}
                {(order.status === 'processing' ||
                  order.status === 'shipped' ||
                  order.status === 'delivered') && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div>
                      <div className="font-medium">Processing</div>
                      <div className="text-sm text-muted-foreground">{order.orderDate}</div>
                    </div>
                  </div>
                )}
                {order.status === 'shipped' && order.trackingNumber && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <div>
                      <div className="font-medium">Shipped</div>
                      <div className="text-sm text-muted-foreground">
                        Tracking: {order.trackingNumber}
                      </div>
                    </div>
                  </div>
                )}
                {order.status === 'delivered' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                    <div>
                      <div className="font-medium">Delivered</div>
                      <div className="text-sm text-muted-foreground">
                        {order.deliveredDate || 'Pending'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Order Notes</CardTitle>
              <NoteDialog
                _orderId={orderId}
                existingNotes={order.notes}
                onAddNote={onAddNote}
                trigger={
                  <Button variant="outline" size="sm">
                    <PlusCircle className="h-4 w-4 mr-1" />
                    Add Note
                  </Button>
                }
              />
            </CardHeader>
            <CardContent>
              {order.notes ? (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No notes added yet</div>
              )}
            </CardContent>
          </Card>

          {/* Update Status Button */}
          <div className="flex justify-end">
            {order.status === 'pending' && (
              <div className="w-full space-y-3">
                {/* Upload Payment Proof Button */}
                {!order.paymentProof && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setIsUploadDialogOpen(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Payment Proof
                  </Button>
                )}

                <Button
                  className="w-full bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => handleStatusUpdate('cancelled')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Order
                </Button>
              </div>
            )}
            {order.status === 'review' && (
              <div className="flex gap-2 w-full">
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => handleStatusUpdate('processing')}
                >
                  <Package className="h-4 w-4 mr-2" />
                  Process Order
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => handleStatusUpdate('cancelled')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Order
                </Button>
              </div>
            )}
            {order.status === 'processing' && (
              <div className="w-full space-y-3">
                {/* Tracking Number Input */}
                <div className="space-y-2">
                  <Label htmlFor="trackingNumber">Tracking Number</Label>
                  <Input
                    id="trackingNumber"
                    value={newTrackingNumber}
                    onChange={(e) => setNewTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 w-full">
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => {
                      if (newTrackingNumber.trim()) {
                        const courier = order?.courier || order?.carrier || order?.shipping || 'jne'
                        onUpdateTracking(orderId, newTrackingNumber.trim(), courier)
                      }
                    }}
                    disabled={!newTrackingNumber.trim()}
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Shipping Order
                  </Button>
                  <Button
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                    onClick={() => handleStatusUpdate('cancelled')}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Order
                  </Button>
                </div>
              </div>
            )}
            {order.status === 'shipped' && (
              <div className="flex gap-2 w-full">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleStatusUpdate('delivered')}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Complete Order
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => handleStatusUpdate('cancelled')}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Order
                </Button>
              </div>
            )}
            {order.status === 'delivered' && (
              <Button
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => handleStatusUpdate('cancelled')}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Refund Order
              </Button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <Label className="font-bold">Name</Label>
                  <p className="font-medium">{order.customerName}</p>
                </div>
                <div>
                  <Label className="font-bold">Email</Label>
                  <p className="text-sm">{order.customerEmail}</p>
                </div>
                <div>
                  <Label className="font-bold">Phone</Label>
                  <p className="text-sm">{order.customerPhone}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Shipping Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div>
                  <Label className="font-bold">Full Name</Label>
                  <p className="font-medium">{order.shippingAddress.fullName}</p>
                </div>
                <div>
                  <Label className="font-bold">Phone</Label>
                  <p className="text-sm">{order.shippingAddress.phone}</p>
                </div>
                <div>
                  <Label className="font-bold">Address</Label>
                  <p className="text-sm">{order.shippingAddress.street}</p>
                  <p className="text-sm">
                    {order.shippingAddress.city}
                    {order.shippingAddress.province &&
                      order.shippingAddress.province !== order.shippingAddress.city && (
                        <>, {order.shippingAddress.province}</>
                      )}{' '}
                    {order.shippingAddress.postalCode}
                  </p>
                  <p className="text-sm">{order.shippingAddress.country}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Payment Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <Label className="font-bold">Method</Label>
                  <p className="text-sm">{formatPaymentMethod(order.paymentMethod)}</p>
                </div>
                <div>
                  <Label className="font-bold">Status</Label>
                  <div className="mt-1">
                    <div
                      className={`inline-flex h-9 items-center rounded-md border px-3 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 gap-1 ${
                        order.paymentStatus === 'paid'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : order.paymentStatus === 'pending'
                            ? 'border-gray-500 bg-gray-50 text-gray-700'
                            : order.paymentStatus === 'failed'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : order.paymentStatus === 'refunded'
                                ? 'border-orange-500 bg-orange-50 text-orange-700'
                                : 'border-purple-500 bg-purple-50 text-purple-700'
                      }`.replace(/\s+/g, ' ')}
                    >
                      {order.paymentStatus}
                    </div>
                  </div>
                </div>
                {/* Payment Proof */}
                {order.paymentProof &&
                  (order.paymentStatus === 'review' || order.paymentStatus === 'paid') && (
                    <div>
                      <Label className="font-bold">Payment Proof</Label>
                      <div className="mt-2">
                        <img
                          src={order.paymentProof}
                          alt="Payment Proof"
                          className="w-full h-auto rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setIsPaymentProofModalOpen(true)}
                          onError={(e) => {
                            // Prevent infinite loop by checking if we've already tried the fallback
                            const target = e.currentTarget
                            if (!target.dataset.fallbackAttempted) {
                              target.dataset.fallbackAttempted = 'true'
                              target.src = '/placeholder-image.png'
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                {/* No Payment Proof Message */}
                {!order.paymentProof &&
                  (order.paymentStatus === 'review' || order.paymentStatus === 'paid') && (
                    <div>
                      <Label className="font-bold">Payment Proof</Label>
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-500">No payment proof image available</p>
                      </div>
                    </div>
                  )}

                {/* Pending Payment Proof Message */}
                {order.paymentStatus === 'pending' && (
                  <div>
                    <Label className="font-bold">Payment Proof</Label>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-500">Payment proof not yet uploaded</p>
                    </div>
                  </div>
                )}

                {/* Payment Proof Modal */}
                <Dialog
                  key="payment-proof-modal"
                  open={isPaymentProofModalOpen}
                  onOpenChange={setIsPaymentProofModalOpen}
                >
                  <DialogContent className="max-w-4xl w-full p-0 gap-0">
                    <div className="flex items-center justify-between p-6 pb-0">
                      <h2 className="text-lg font-semibold leading-none tracking-tight m-0">
                        Payment Proof
                      </h2>
                    </div>
                    <div className="flex justify-center pt-0">
                      <img
                        src={order.paymentProof}
                        alt="Payment Proof Full Size"
                        className="max-w-full max-h-[80vh] object-contain rounded-lg"
                        onError={(e) => {
                          // Prevent infinite loop by checking if we've already tried the fallback
                          const target = e.currentTarget
                          if (!target.dataset.fallbackAttempted) {
                            target.dataset.fallbackAttempted = 'true'
                            target.src = '/placeholder-image.png'
                          }
                        }}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          {/* Tracking */}
          <Card>
            <CardHeader>
              <CardTitle>Tracking Information</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Carrier Information */}
              {(order.carrier || order.courier || order.shipping) && (
                <div>
                  <Label className="font-bold">Carrier</Label>
                  <div className="text-sm">
                    {(() => {
                      const carrierParts = []
                      if (order.carrier) carrierParts.push(order.carrier.toUpperCase())
                      if (order.courier && order.courier !== order.carrier)
                        carrierParts.push(order.courier)
                      if (
                        order.shipping &&
                        order.shipping !== order.carrier &&
                        order.shipping !== order.courier
                      )
                        carrierParts.push(order.shipping)
                      return carrierParts.join(' • ')
                    })()}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <Label className="font-bold">Tracking Number</Label>
                {order.trackingNumber ? (
                  <p className="font-mono text-sm">{order.trackingNumber}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Not available</p>
                )}
              </div>
              {order.estimatedDelivery && (
                <div>
                  <Label>Estimated Delivery</Label>
                  <p className="text-sm">{order.estimatedDelivery}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payment Proof Modal */}
      {order.paymentProof && (
        <Dialog open={isPaymentProofModalOpen} onOpenChange={setIsPaymentProofModalOpen}>
          <DialogContent className="max-w-screen-xl h-[90vh]">
            <DialogHeader>
              <DialogTitle>Payment Proof</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center items-center h-full">
              <img
                src={order.paymentProof}
                alt="Payment Proof"
                className="max-h-full max-w-full object-contain"
                onError={(e) => {
                  // Prevent infinite loop by checking if we've already tried the fallback
                  const target = e.currentTarget
                  if (!target.dataset.fallbackAttempted) {
                    target.dataset.fallbackAttempted = 'true'
                    target.src = '/placeholder-image.png'
                  }
                }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Upload Payment Proof Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-md w-full">
          <DialogHeader>
            <DialogTitle>Upload Payment Proof</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="proof-image">Payment Proof Image</Label>
              <Input
                id="proof-image"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="mt-2"
              />
            </div>

            {uploadPreview && (
              <div>
                <Label>Preview</Label>
                <div className="mt-2">
                  <img
                    src={uploadPreview}
                    alt="Payment proof preview"
                    className="w-full h-auto rounded-lg border border-gray-200"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadDialogOpen(false)
                  setSelectedFile(null)
                  setUploadPreview(null)
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadPaymentProof}
                disabled={!selectedFile || uploadPaymentProofMutation.isPending}
              >
                {uploadPaymentProofMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
